import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiService } from 'app/core/api/api.service';
import { BehaviorSubject, Observable, tap, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VenueService {
    private _data: BehaviorSubject<any> = new BehaviorSubject(null);
    private _apiService = inject(ApiService);
    private _updated = new Subject<Object>();
    updated$ = this._updated.asObservable();
    private dataStore: { venues: any[] } = { venues: [] };
    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for data
     */
    get data$(): Observable<any> {
        return this._data.asObservable();
    }

    get(): any {
        return this.dataStore.venues;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------


    /**
     * Get all venues for sidebar
     */
    getVenues(): Observable<any> {
        return this._apiService.get('venue').pipe(
            tap((venues: any) => {
                this._data.next(venues); 
                this._updated.next(venues);
                this.dataStore.venues = venues;
            })
        );
    }

    getVenueById(id: string): Observable<any> {
        return this._apiService.get('venue/'+id).pipe(
            tap((venues: any) => {
                this._data.next(venues);
            })
        );
    }

    /**
     * Get venue with cancellation policy and privacy policy
     */
    getVenueWithPolicies(id: string): Observable<any> {
        return this._apiService.get(`venue/${id}`);
    }

    /**
     * Get all locations for dropdown
     */
    getLocations(): Observable<any> {
        return this._apiService.get('locations');
    }

    /**
     * Create a new venue
     */
    createOrUpdateVenue(venueData: any): Observable<any> {
        if (venueData.id !== undefined) {
            const { id, ...updateData } = venueData;
            return this._apiService.put(`venue/${id}`, updateData);
        } else { 
            return this._apiService.post('venue', venueData);
        }
        
    }

     /**
     * Create a new court
     */
    delete(venueId: string): Observable<any> {
        return this._apiService.delete(`venue/${venueId}`);
    }
    /**
     * Get all venues regardless of company
     */
    getAllVenues(queryParams?: {
        limit?: number;
        page?: number;
        search?: string;
    }): Observable<any> {
        let queryString = '';

        if (queryParams) {
            const params = new URLSearchParams();

            if (queryParams.limit) {
                params.append('limit', queryParams.limit.toString());
            }

            if (queryParams.page) {
                params.append('page', queryParams.page.toString());
            }

            if (queryParams.search?.trim()) {
                params.append('search', queryParams.search.trim());
            }

            queryString = params.toString() ? `?${params.toString()}` : '';
        }

        return this._apiService.get(`venue/all${queryString}`).pipe(
            tap((response: any) => {
                const venues = Array.isArray(response)
                    ? response
                    : response?.results || response?.data || [];
                this._data.next(venues); 
                this._updated.next(venues);
                this.dataStore.venues = venues;
            })
        );
    }


getCourtsByVenue(venueId: string): Observable<any> {
  return this._apiService.get(`venue/${venueId}/courts/superadmin/all`);
}

/**
 * Verify a venue
 * PATCH {{baseUrl}}/v1/venue/{venueId}/verify
 */
verifyVenue(venueId: string, verifyStatus: boolean = true): Observable<any> {
    return this._apiService.patch(`venue/${venueId}/verify`, { verified: verifyStatus });
}

/** Set the venue onboarding status through its privileged endpoint. */
setVenueOnboarding(venueId: string, onBoarded: boolean): Observable<any> {
    return this._apiService.patch(`venue/${venueId}/onboarding`, { onBoarded });
}

/** Set or unset platform-managed featured placement for a venue. */
setVenueFeatured(venueId: string, featured: boolean): Observable<any> {
    return this._apiService.patch(`venue/${venueId}/featured`, { featured });
}
/**
     * NEW: Delete venue as super admin
     * @param venueId - The ID of the venue to delete
     * @returns Observable with the deletion result
     */
    deleteVenueBySuperAdmin(venueId: string): Observable<any> {
        return this._apiService.delete(`venue/${venueId}/super-admin`);
    }

}

